import { CSSObject, Stack, StackProps, styled, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { ReactNode } from 'react';
import { GLOBAL_TABS_HEIGHT } from 'src/components/GlobalTabs/styled';
import { DrawerCloseControl } from 'src/components/Navbar/DesktopDrawer/drawer-close-control';

export type BlockContainerProps = {
  leftRoundedOnly?: boolean;
  rightRoundedOnly?: boolean;
  framed?: boolean;
};

const framedBlockStyles = (theme: Theme): CSSObject => ({
  borderRadius: '0.75rem',
  border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
});

export const BlockContainer = styled(Stack, {
  name: 'NamedLayoutBlockContainer',
  shouldForwardProp: (prop) =>
    prop !== 'leftRoundedOnly' && prop !== 'rightRoundedOnly' && prop !== 'framed',
})<StackProps & BlockContainerProps>(({
  theme,
  leftRoundedOnly,
  rightRoundedOnly,
  framed,
}) => {
  const cssObj: CSSObject = {};

  if (leftRoundedOnly) {
    cssObj.borderTopRightRadius = 0;
    cssObj.borderBottomRightRadius = 0;
    cssObj.borderRight = `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`;
  }

  if (rightRoundedOnly) {
    cssObj.borderTopLeftRadius = 0;
    cssObj.borderBottomLeftRadius = 0;
    cssObj.borderLeft = `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`;
  }

  if (framed) {
    Object.assign(cssObj, framedBlockStyles(theme));
  }

  return {
    height: '100%',
    width: '100%',
    overflow: 'hidden',

    [theme.breakpoints.down('sm')]: {
      border: 'none',
      borderRadius: 0,
    },
    ...cssObj,
  };
});

// OuterBlockContainer is used as a top-level container, should not be used anywhere else.
export const OuterBlockContainer = styled(BlockContainer)(({ theme }) => ({
  ...framedBlockStyles(theme),
  margin: '0.25rem 0.25rem 0.25rem 0',
  width: 'auto',

  [theme.breakpoints.down('sm')]: {
    margin: 0,
  },

  // when a page frames its own cards (e.g. conversations side-peek), drop this outer frame to avoid card-in-card
  '&:has([data-split-mode])': {
    border: 'none',
    borderRadius: 0,
  },
}));

export const BlockHeader = styled(Stack, {
  name: 'NamedLayoutBlockHeader',
})(({ theme }) => ({
  flexDirection: 'row',
  gap: '0.5rem',
  alignItems: 'center',
  height: GLOBAL_TABS_HEIGHT,
  flex: '0 0 auto',
  padding: '0 0.5rem',
  backgroundColor: theme.palette.custom.OV.sys.surfaceContainerLowest,
  [theme.breakpoints.up('sm')]: {
    borderBottom: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
  },
}));

export const BlockContent = styled(Stack, {
  name: 'NamedLayoutBlockContent',
})(({ theme }) => ({
  // TODO: Investigate if this is necessary and maybe remove this condition.
  // Currently it may affect too many places if removed, so 'static' position is
  // set manually on several `BlockContent` components.
  position: 'relative',
  flex: 1,
  padding: `var(--block-content-padding, ${theme.spacing(1)})`,
  backgroundColor: theme.palette.custom.OV.sys.surfaceContainerLowest,
  overflow: 'auto',
}));

type NamedLayoutBlockProps = {
  title?: string;
  customTitle?: ReactNode;
  leftHeaderAdornment?: ReactNode;
  rightHeaderAdornment?: ReactNode;
  BlockContentProps?: StackProps;
  children?: ReactNode;
  subHeader?: ReactNode;
} & StackProps &
  BlockContainerProps;

export const NamedLayoutBlock = (props: NamedLayoutBlockProps) => {
  const {
    leftHeaderAdornment,
    rightHeaderAdornment,
    title,
    subHeader,
    children,
    customTitle,
    BlockContentProps,
    ...rest
  } = props;

  const renderTitle = () => {
    if (title) {
      return (
        <Typography
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          flex="1"
          variant="subtitle2"
        >
          {title}
        </Typography>
      );
    }

    if (customTitle) {
      return customTitle;
    }

    return null;
  };

  return (
    <BlockContainer {...rest}>
      <BlockHeader>
        <DrawerCloseControl />
        {leftHeaderAdornment}
        {renderTitle()}
        {rightHeaderAdornment}
      </BlockHeader>
      {!!subHeader && <BlockHeader>{subHeader}</BlockHeader>}
      <BlockContent {...BlockContentProps}>{children}</BlockContent>
    </BlockContainer>
  );
};
